/**
 * The languages the apps ship catalogues for, and the bootstrap override that selects one.
 *
 * `SUPPORTED_LANGS` is the single source for both `availableLangs` and the values accepted from
 * storage, so the two cannot drift apart: dropping a language here stops it being accepted and
 * existing stored values for it fall back to `DEFAULT_LANG`.
 */
export const SUPPORTED_LANGS = ['de', 'en'] as const;

export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

export const DEFAULT_LANG: SupportedLang = 'de';

/**
 * Follows the `config.*` namespace of the UI debug flag (`config.debug`, owned by
 * `shared/util-runtime-config`), so a developer finds both in the same place in DevTools.
 */
export const LANG_STORAGE_KEY = 'config.lang';

export function isSupportedLang(value: string): value is SupportedLang {
    return (SUPPORTED_LANGS as readonly string[]).includes(value);
}

/**
 * Reads the language to start in. Anything other than a supported code - key absent, empty,
 * whitespace, an unknown code, or a storage that cannot be read at all - yields `DEFAULT_LANG`.
 *
 * The try/catch is deliberate and differs from `debug-config.ts`, which calls `getItem` bare: this
 * runs while Transloco's providers are being constructed, so a throw would stop the app from
 * starting rather than just mis-reading a flag. Safari's private mode and blocked site data both
 * throw on access.
 */
export function readStoredLang(): SupportedLang {
    let raw: string | null;
    try {
        raw = localStorage.getItem(LANG_STORAGE_KEY);
    } catch {
        return DEFAULT_LANG;
    }

    const normalized = raw?.trim().toLowerCase() ?? '';
    return isSupportedLang(normalized) ? normalized : DEFAULT_LANG;
}

/**
 * Persists the chosen language so a reload keeps it. Guarded for the same reason as the read: an
 * inaccessible store must not turn a language switch into an exception.
 */
export function writeStoredLang(lang: SupportedLang): void {
    try {
        localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
        // Nothing to do - the switch still applies to the running app, it just will not survive a reload.
    }
}

/** Removes the override. Absence of the key is the default state, matching `config.debug`. */
export function clearStoredLang(): void {
    try {
        localStorage.removeItem(LANG_STORAGE_KEY);
    } catch {
        // See writeStoredLang.
    }
}
